import { useGetDeviceRemoteDesktopSupportQuery } from '../api/DeviceConfigurationAPI';
import useDevicesRemoteDesktopSupport from './useDevicesRemoteDesktopSupport';
import { RemoteDesktopStatus } from '../api/adapter/DeviceRemoteDesktopAdapter';
import { useAppSelector } from '../../../store.model';
import { getRightUserUtilSelector } from '../../../rights/selectors';

/**
 * Hook to check if a single device supports remote desktop access.
 *
 * This hook is optimized to work in two scenarios:
 * 1. When used in a list context where useDevicesRemoteDesktopSupport is already fetching bulk data:
 *    - Extracts the specific device from the bulk query cache
 *    - No additional API call needed
 *
 * 2. When used in a detail view context with a single device:
 *    - Falls back to individual device query if bulk data not available
 *    - Fetches only for the specific device
 *
 * Returns loading state, error state, and status enum.
 * Results are cached by device ID per RTK Query behavior.
 *
 * @param deviceId - The device ID to check
 * @returns { isLoading, error, status, reason }
 */
export const useDeviceRemoteDesktopSupport = (deviceId: string | undefined) => {
  const rightsUserUtil = useAppSelector(getRightUserUtilSelector);
  const hasRemoteDesktopUsageRight =
    rightsUserUtil.hasRightsToRemoteDesktopUsage();

  // Try to get from bulk query cache first (if available)
  const { data: bulkData, isLoading: bulkLoading } =
    useDevicesRemoteDesktopSupport();

  // Fallback to individual query if bulk data not available
  const {
    data: singleData,
    isLoading: singleLoading,
    error,
  } = useGetDeviceRemoteDesktopSupportQuery(deviceId ?? '', {
    skip:
      !deviceId ||
      !hasRemoteDesktopUsageRight ||
      (bulkData?.entities && bulkData.entities[deviceId!] !== undefined),
  });

  // Check if device support info exists in bulk cache
  const bulkCacheData =
    deviceId && bulkData?.entities ? bulkData.entities[deviceId] : undefined;

  // Use bulk cache if available, otherwise use single query result
  const data = bulkCacheData || singleData;
  const isLoading =
    deviceId && !data ? (bulkCacheData ? bulkLoading : singleLoading) : false;

  return {
    isLoading,
    error,
    status: data?.status ?? RemoteDesktopStatus.NOT_SUPPORTED,
    reason: data?.reason,
  };
};
